import { Link, useLocation, useNavigate } from "react-router-dom";
import logo from "../assets/images/visaro-logo.svg";
import { useCallback, useEffect, useState } from "react";
import * as yup from "yup";
import { ErrorMessage, Field, Formik, Form } from "formik";
import useResetPassword from "../hooks/useResetPassword";
import { HashLoader } from "react-spinners";
import PasswordCheck from "../components/PasswordCheck";
import useForgotPassword from "../hooks/useForgotPassword";
import { toast } from "sonner";

const schema = yup.object().shape({
  code: yup.string().required("Code is required. Check your email").length(4),
  password: yup
    .string()
    .min(8, "Password must be at least 8 characters")
    .required("Password is required"),
  confirmPassword: yup
    .string()
    .oneOf([yup.ref("password")], "Passwords must match")
    .required("Confirm password is required"),
});

const ResetPassword = () => {
  useEffect(() => {
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: "smooth",
    });
  }, []);

  const { state } = useLocation();
  const [email, setEmail] = useState("");
  const navigate = useNavigate();
  const [passwordIsVisible, setPasswordIsVisible] = useState(false);
  const [timer, setTimer] = useState(50);
  const [resendEnabled, setResendEnabled] = useState(false);

  useEffect(() => {
    let interval: any;
    if (timer > 0) {
      interval = setInterval(() => {
        setTimer((prevTimer) => prevTimer - 1);
      }, 1000);
    } else {
      clearInterval(interval);
      setResendEnabled(true);
    }
    return () => clearInterval(interval);
  }, [timer]);

  useEffect(() => {
    const storedEmail = state?.email;

    if (storedEmail) {
      setEmail(storedEmail);
    } else {
      navigate("/forgot-password");
    }
  }, [state, navigate]);

  const { resetPassword, error, isError, isLoading } = useResetPassword({
    onSuccess() {
      toast.success("Password reset successfully");
      navigate("/login");
    },
  });

  const {
    forgotPassword,
    error: forgotPasswordError,
    isError: isForgotPasswordError,
    isLoading: isLoadingForgotPassword,
  } = useForgotPassword();

  const handleResend = useCallback(() => {
    forgotPassword(
      { email },
      {
        onSuccess() {
          setTimer(60);
          setResendEnabled(false);
        },
      }
    );
  }, [forgotPassword, email]);

  return (
    <>
      {isLoading && (
        <div className="fixed top-0 z-50 left-0 h-screen w-screen flex items-center justify-center bg-black/20 backdrop-blur-sm">
          <HashLoader />
        </div>
      )}
      <Formik
        initialValues={{
          password: "",
          confirmPassword: "",
          code: "",
        }}
        validationSchema={schema}
        onSubmit={(values, { resetForm }) => {
          resetForm();
          resetPassword({
            email: email,
            password: values.password,
            code: String(values.code),
          });
        }}
      >
        {({ isValid, dirty, values, setTouched }) => {
          return (
            <div>
              <nav className="p-4 md:px-10 flex items-center justify-center border border-gray-100">
                <div className="max-w-3xl w-full flex items-center justify-between">
                  <Link to="/">
                    <img src={logo} alt="Visaro's Logo" />
                  </Link>
                  <div></div>
                  <div className="hidden md:flex items-center gap-4">
                    <div></div>
                    <Link
                      to="/login"
                      className="p-3 px-5 border-deepBlue border rounded-lg font-bold text-sm text-deepBlue"
                    >
                      Log in
                    </Link>
                  </div>
                </div>
              </nav>
              <div className="flex flex-col justify-center items-center p-20 px-5 md:px-20 gap-8 text-lg">
                <div className="flex flex-col justify-center items-center gap-2">
                  <div className="text-4xl md:text-3xl lg:text-5xl max-w-3xl font-bold text-center">
                    Reset password
                  </div>
                  <div className="text-gray-500 text-center max-w-lg">
                    Create a new password and keeps your account secure.
                  </div>
                </div>
                <Form className="w-full flex-col gap-5 flex items-center justify-center text-base">
                  <div className="flex flex-col gap-2 max-w-md w-full">
                    <label
                      htmlFor="code"
                      className="text-sm font-bold text-gray-700"
                    >
                      OTP Code*
                    </label>
                    <Field
                      type="text"
                      inputMode="numeric"
                      name="code"
                      id="code"
                      placeholder="Enter the 4-digits OTP sent to your email"
                      className="rounded-lg border border-gray-300 shadow px-4 py-3 w-full"
                    />
                    <ErrorMessage
                      name="code"
                      component="div"
                      className="text-red-500"
                    />
                    {!resendEnabled && (
                      <div className="text-gray-500">
                        Resend in {timer} seconds
                      </div>
                    )}
                    {resendEnabled &&
                      (isLoadingForgotPassword ? (
                        <div className="text-gray-500">Resending OTP...</div>
                      ) : (
                        <div className="text-gray-500">
                          Didn’t get a code?{" "}
                          <span
                            className="underline"
                            onClick={handleResend}
                            style={{ cursor: "pointer" }}
                          >
                            Click to resend.
                          </span>
                        </div>
                      ))}
                    {isForgotPasswordError && (
                      <div className="text-red-500">
                        {forgotPasswordError?.message}
                      </div>
                    )}
                  </div>
                  <div className="flex flex-col gap-2 max-w-md w-full">
                    <label
                      htmlFor="password"
                      className="text-sm font-bold text-gray-700"
                    >
                      Password*
                    </label>
                    <div className="rounded-lg border border-gray-300 shadow flex items-center w-full relative overflow-hidden focus-within:outline-deepBlue focus-within:outline accent-deepBlue">
                      <Field
                        onFocus={() =>
                          setTouched(
                            {
                              password: true,
                            },
                            false
                          )
                        }
                        autoComplete="off"
                        type={passwordIsVisible ? "text" : "password"}
                        id="password"
                        name="password"
                        placeholder="Create a password"
                        className="px-4 py-3 bg-transparent flex-1 rounded-lg focus:outline-none"
                      />
                      <div
                        onClick={() => setPasswordIsVisible((prev) => !prev)}
                        className="absolute right-0 m-4 cursor-pointer text-sm"
                      >
                        {passwordIsVisible ? "Hide" : "Show"}
                      </div>
                    </div>
                    <ErrorMessage
                      name="password"
                      component="div"
                      className="text-red-500"
                    />
                  </div>
                  <div className="flex flex-col gap-2 max-w-md w-full">
                    <label
                      htmlFor="confirmPassword"
                      className="text-sm font-bold text-gray-700"
                    >
                      Confirm Password*
                    </label>
                    <div className="rounded-lg border border-gray-300 shadow flex items-center w-full relative overflow-hidden focus-within:outline-deepBlue focus-within:outline accent-deepBlue">
                      <Field
                        onFocus={() =>
                          setTouched(
                            {
                              password: true,
                            },
                            false
                          )
                        }
                        autoComplete="off"
                        type={passwordIsVisible ? "text" : "password"}
                        id="confirmPassword"
                        name="confirmPassword"
                        placeholder="Confirm Password"
                        className="px-4 py-3 bg-transparent flex-1 rounded-lg focus:outline-none"
                      />
                      <div
                        onClick={() => setPasswordIsVisible((prev) => !prev)}
                        className="absolute right-0 m-4 cursor-pointer text-sm"
                      >
                        {passwordIsVisible ? "Hide" : "Show"}
                      </div>
                    </div>
                    <ErrorMessage
                      name="confirmPassword"
                      component="div"
                      className="text-red-500"
                    />
                    <PasswordCheck password={values.password} />
                  </div>

                  <div className="flex flex-col gap-2 max-w-md w-full">
                    {isError && (
                      <div className="text-red-500 text-left flex">
                        {error?.message}
                      </div>
                    )}
                    <button
                      disabled={!isValid || !dirty}
                      type="submit"
                      className="p-4 px-5 w-full border-visaro-gray-700 border rounded-lg font-bold bg-deepBlue text-white text-sm"
                    >
                      {isLoading ? "Loading" : "Continue"}
                    </button>
                  </div>
                </Form>
              </div>
            </div>
          );
        }}
      </Formik>
    </>
  );
};

export default ResetPassword;
